import { useCallback, useEffect, useRef, useState } from "react";
import type { Canvas, RawCanvas } from "../../../shared/types";
import { useToast } from "../contexts/ToastContext";
import { getCanvases } from "../lib/api";

export function useCanvases(token: string | null, userId: number | null) {
  const [canvases, setCanvases] = useState<Canvas[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addToast } = useToast();

  // Track IDs where it is currently the user's turn
  const prevMyTurnIds = useRef<Set<number> | null>(null);
  const lastRefreshTime = useRef<number>(0);

  const refresh = useCallback(async () => {
    if (!token) return;

    // Avoid refreshing too frequently (e.g. multiple events firing at once)
    const now = Date.now();
    if (now - lastRefreshTime.current < 500) return;
    lastRefreshTime.current = now;

    // Don't set loading to true for background refreshes (unless it's the initial load)
    if (prevMyTurnIds.current === null) setLoading(true);

    try {
      const data = await getCanvases();
      if (!Array.isArray(data)) {
        console.error("Unexpected response from getCanvases:", data);
        setCanvases([]);
        return;
      }
      const newCanvases = data.map((c: RawCanvas) => ({
        ...c,
        collaborators: JSON.parse(c.collaborators),
      }));

      // Check for new turns
      if (userId) {
        const currentMyTurnIds = new Set(
          newCanvases
            .filter((c: Canvas) => c.current_turn_user_id === userId)
            .map((c: Canvas) => c.id),
        );

        if (prevMyTurnIds.current !== null) {
          const newTurns = [...currentMyTurnIds].filter((id) => !prevMyTurnIds.current?.has(id));
          if (newTurns.length > 0) {
            addToast("New note passed!", "info", 3000);
          }
        }
        prevMyTurnIds.current = currentMyTurnIds;
      }

      setCanvases(newCanvases);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token, userId, addToast]);

  // Initial fetch
  useEffect(() => {
    if (token) {
      refresh();
    } else {
      setCanvases([]);
      prevMyTurnIds.current = null;
    }
  }, [token, refresh]);

  // Server Events (SSE)
  useEffect(() => {
    if (!token) return;

    const es = new EventSource(`/api/events?token=${encodeURIComponent(token)}`);

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "turnChanged" || data.type === "canvasChanged") {
          refresh();
        }
      } catch {}
    };

    return () => es.close();
  }, [token, refresh]);

  // Handle focus and visibility changes (crucial for PWA/iOS)
  useEffect(() => {
    if (!token) return;

    const handleUpdate = () => {
      refresh();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleUpdate();
      }
    };

    window.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleUpdate);
    window.addEventListener("pageshow", handleUpdate);
    window.addEventListener("online", handleUpdate);

    return () => {
      window.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleUpdate);
      window.removeEventListener("pageshow", handleUpdate);
      window.removeEventListener("online", handleUpdate);
    };
  }, [token, refresh]);

  return { canvases, refresh, loading, error, setCanvases };
}
